import { requireSupabase } from "@/lib/supabaseClient";
import { guard } from "@/lib/errors";

const PROPERTY_SELECT = `*, images:property_images(*), category:property_categories(*), type:property_types(*)`;

export const propertyService = {
  list: ({ page = 0, pageSize = 12, deal, cityId, regionId, zoneId, subCityId, woredaId, neighborhoodId, typeId, categoryId, amenityIds, minPrice, maxPrice, bedrooms, bathrooms, minSize, minParking, verified, premium, agencyId, developerId, minLat, maxLat, minLng, maxLng, hasGeo, search, featured, onlyPublished = true, sort = "newest" } = {}) =>
    guard("property.list", async () => {
      const s = requireSupabase();
      const from = page * pageSize, to = from + pageSize - 1;

      // Optional amenity filter (match ANY selected amenity) via join table.
      let amenityPropertyIds = null;
      if (Array.isArray(amenityIds) && amenityIds.length) {
        const { data: rows, error: aErr } = await s.from("property_amenities").select("property_id").in("amenity_id", amenityIds);
        if (aErr) throw aErr;
        amenityPropertyIds = [...new Set((rows || []).map((r) => r.property_id))];
        if (!amenityPropertyIds.length) return { data: [], count: 0, page, pageSize };
      }

      let q = s.from("properties").select(PROPERTY_SELECT, { count: "exact" });
      if (onlyPublished) q = q.eq("published", true);
      if (deal) q = q.eq("deal", deal);
      if (cityId) q = q.eq("city_id", cityId);
      if (regionId) q = q.eq("region_id", regionId);
      if (zoneId) q = q.eq("zone_id", zoneId);
      if (subCityId) q = q.eq("sub_city_id", subCityId);
      if (woredaId) q = q.eq("woreda_id", woredaId);
      if (neighborhoodId) q = q.eq("neighborhood_id", neighborhoodId);
      if (typeId) q = q.eq("type_id", typeId);
      if (categoryId) q = q.eq("category_id", categoryId);
      if (featured) q = q.eq("featured", true);
      if (premium) q = q.eq("featured", true);
      if (verified) q = q.eq("verified", true);
      if (agencyId) q = q.eq("agency_id", agencyId);
      if (developerId) q = q.eq("developer_id", developerId);
      if (typeof minParking === "number") q = q.gte("parking", minParking);
      if (amenityPropertyIds) q = q.in("id", amenityPropertyIds);
      if (typeof minPrice === "number") q = q.gte("price", minPrice);
      if (typeof maxPrice === "number") q = q.lte("price", maxPrice);
      if (typeof bedrooms === "number") q = q.gte("bedrooms", bedrooms);
      if (typeof bathrooms === "number") q = q.gte("bathrooms", bathrooms);
      if (typeof minSize === "number") q = q.gte("square_meters", minSize);
      if (hasGeo) q = q.not("latitude", "is", null).not("longitude", "is", null);
      if (typeof minLat === "number") q = q.gte("latitude", minLat);
      if (typeof maxLat === "number") q = q.lte("latitude", maxLat);
      if (typeof minLng === "number") q = q.gte("longitude", minLng);
      if (typeof maxLng === "number") q = q.lte("longitude", maxLng);
      if (search) q = q.or(`title.ilike.%${search}%,address.ilike.%${search}%,description.ilike.%${search}%`);
      if (sort === "price_asc") q = q.order("price", { ascending: true });
      else if (sort === "price_desc") q = q.order("price", { ascending: false });
      else if (sort === "popular") q = q.order("views_count", { ascending: false });
      else q = q.order("created_at", { ascending: false });
      q = q.range(from, to);
      const { data, error, count } = await q;
      if (error) throw error;
      return { data: data || [], count: count || 0, page, pageSize };
    }),

  // Lightweight fetch of located properties for the map layer (no pagination).
  listGeo: ({ deal, cityId, regionId, zoneId, subCityId, woredaId, neighborhoodId, typeId, categoryId, minPrice, maxPrice, bedrooms, bathrooms, minSize, minParking, verified, premium, agencyId, developerId, search, limit = 500 } = {}) =>
    guard("property.listGeo", async () => {
      const s = requireSupabase();
      let q = s.from("properties")
        .select("id, title, price, currency, deal, latitude, longitude, address, bedrooms, bathrooms, square_meters, featured, verified, images:property_images(url,is_cover)")
        .eq("published", true)
        .not("latitude", "is", null).not("longitude", "is", null);
      if (deal) q = q.eq("deal", deal);
      if (cityId) q = q.eq("city_id", cityId);
      if (regionId) q = q.eq("region_id", regionId);
      if (zoneId) q = q.eq("zone_id", zoneId);
      if (subCityId) q = q.eq("sub_city_id", subCityId);
      if (woredaId) q = q.eq("woreda_id", woredaId);
      if (neighborhoodId) q = q.eq("neighborhood_id", neighborhoodId);
      if (typeId) q = q.eq("type_id", typeId);
      if (categoryId) q = q.eq("category_id", categoryId);
      if (premium) q = q.eq("featured", true);
      if (verified) q = q.eq("verified", true);
      if (agencyId) q = q.eq("agency_id", agencyId);
      if (developerId) q = q.eq("developer_id", developerId);
      if (typeof minParking === "number") q = q.gte("parking", minParking);
      if (typeof minPrice === "number") q = q.gte("price", minPrice);
      if (typeof maxPrice === "number") q = q.lte("price", maxPrice);
      if (typeof bedrooms === "number") q = q.gte("bedrooms", bedrooms);
      if (typeof bathrooms === "number") q = q.gte("bathrooms", bathrooms);
      if (typeof minSize === "number") q = q.gte("square_meters", minSize);
      if (search) q = q.or(`title.ilike.%${search}%,address.ilike.%${search}%,description.ilike.%${search}%`);
      const { data, error } = await q.limit(limit);
      if (error) throw error;
      return data || [];
    }),

  getById: (id) => guard("property.getById", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties")
      .select(`${PROPERTY_SELECT}, videos:property_videos(*), documents:property_documents(*)`)
      .eq("id", id).maybeSingle();
    if (error) throw error; return data;
  }),

  similar: (property, limit = 3) => guard("property.similar", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").select(PROPERTY_SELECT)
      .eq("published", true).eq("deal", property.deal).neq("id", property.id)
      .order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),

  mine: (userId) => guard("property.mine", async () => {
    const s = requireSupabase();
    let q = s.from("properties").select(PROPERTY_SELECT);
    if (userId) q = q.eq("created_by", userId);
    const { data, error } = await q.order("created_at", { ascending: false });
    if (error) throw error; return data || [];
  }),

  // Published listings owned by an account — used on public profile pages.
  publicByOwner: (userId, { limit = 24 } = {}) => guard("property.publicByOwner", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").select(PROPERTY_SELECT)
      .eq("created_by", userId).eq("published", true)
      .order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),

  publicByAgency: (agencyId, { limit = 24 } = {}) => guard("property.publicByAgency", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").select(PROPERTY_SELECT)
      .eq("agency_id", agencyId).eq("published", true)
      .order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),

  publicByDeveloper: (developerId, { limit = 24 } = {}) => guard("property.publicByDeveloper", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").select(PROPERTY_SELECT)
      .eq("developer_id", developerId).eq("published", true)
      .order("created_at", { ascending: false }).limit(limit);
    if (error) throw error; return data || [];
  }),

  // Listing lifecycle — no duplicate action; state transitions only.
  setStatus: (id, status) => guard("property.setStatus", async () => {
    const s = requireSupabase();
    const patch = { status };
    if (status === "published") { patch.published = true; patch.published_at = new Date().toISOString(); }
    else if (["archived", "rejected", "expired", "sold", "rented"].includes(status)) { patch.published = false; }
    const { data, error } = await s.from("properties").update(patch).eq("id", id).select().maybeSingle();
    if (error) throw error; return data;
  }),

  archive: (id) => propertyService.setStatus(id, "archived"),
  republish: (id) => propertyService.setStatus(id, "published"),

  // Renew: extend expiry and (re)publish so the listing stays live.
  renew: (id, days = 30) => guard("property.renew", async () => {
    const s = requireSupabase();
    const expires = new Date(Date.now() + days * 86400000).toISOString();
    const { data, error } = await s.from("properties")
      .update({ status: "published", published: true, published_at: new Date().toISOString(), expires_at: expires })
      .eq("id", id).select().maybeSingle();
    if (error) throw error; return data;
  }),

  // Admin-only ownership transfer.
  transferOwner: (id, newOwnerId) => guard("property.transferOwner", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").update({ created_by: newOwnerId }).eq("id", id).select().maybeSingle();
    if (error) throw error; return data;
  }),

  // Newest draft (status='draft') owned by the user — used to resume an
  // unfinished listing across devices/sessions.
  myLatestDraft: (userId) => guard("property.myLatestDraft", async () => {
    if (!userId) return null;
    const s = requireSupabase();
    const { data, error } = await s.from("properties")
      .select(PROPERTY_SELECT)
      .eq("created_by", userId).eq("status", "draft")
      .order("updated_at", { ascending: false }).limit(1).maybeSingle();
    if (error) throw error; return data;
  }),

  create: (payload) => guard("property.create", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").insert(payload).select().maybeSingle();
    if (error) throw error; return data;
  }),

  update: (id, patch) => guard("property.update", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("properties").update(patch).eq("id", id).select().maybeSingle();
    if (error) throw error; return data;
  }),

  remove: (id) => guard("property.remove", async () => {
    const s = requireSupabase();
    const { error } = await s.from("properties").delete().eq("id", id);
    if (error) throw error; return true;
  }),

  addImages: (propertyId, images = []) => guard("property.addImages", async () => {
    if (!images.length) return [];
    const s = requireSupabase();
    const rows = images.map((img, i) => ({
      property_id: propertyId, url: img.publicUrl, storage_path: img.path,
      is_cover: img.isCover || (img.sortOrder ?? i) === 0, sort_order: img.sortOrder ?? i,
    }));
    const { data, error } = await s.from("property_images").insert(rows).select();
    if (error) throw error; return data;
  }),

  // Update a single image row (cover flag / ordering).
  updateImage: (imageId, patch) => guard("property.updateImage", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("property_images").update(patch).eq("id", imageId).select().maybeSingle();
    if (error) throw error; return data;
  }),

  // Delete a single image row (storage object cleaned up separately).
  removeImage: (imageId) => guard("property.removeImage", async () => {
    const s = requireSupabase();
    const { error } = await s.from("property_images").delete().eq("id", imageId);
    if (error) throw error; return true;
  }),

  addVideos: (propertyId, videos = []) => guard("property.addVideos", async () => {
    if (!videos.length) return [];
    const s = requireSupabase();
    const rows = videos.map((v, i) => ({ property_id: propertyId, url: v.publicUrl, storage_path: v.path, sort_order: i }));
    const { data, error } = await s.from("property_videos").insert(rows).select();
    if (error) throw error; return data;
  }),

  addDocuments: (propertyId, docs = []) => guard("property.addDocuments", async () => {
    if (!docs.length) return [];
    const s = requireSupabase();
    const rows = docs.map((d) => ({ property_id: propertyId, name: d.name, url: d.publicUrl, storage_path: d.path }));
    const { data, error } = await s.from("property_documents").insert(rows).select();
    if (error) throw error; return data;
  }),

  setAmenities: (propertyId, amenityIds = []) => guard("property.setAmenities", async () => {
    if (!amenityIds.length) return true;
    const s = requireSupabase();
    const rows = amenityIds.map((amenity_id) => ({ property_id: propertyId, amenity_id }));
    const { error } = await s.from("property_amenities").insert(rows);
    if (error) throw error; return true;
  }),

  // Replace the full amenity set for a property (used when editing).
  replaceAmenities: (propertyId, amenityIds = []) => guard("property.replaceAmenities", async () => {
    const s = requireSupabase();
    const { error: delErr } = await s.from("property_amenities").delete().eq("property_id", propertyId);
    if (delErr) throw delErr;
    if (!amenityIds.length) return true;
    const rows = amenityIds.map((amenity_id) => ({ property_id: propertyId, amenity_id }));
    const { error } = await s.from("property_amenities").insert(rows);
    if (error) throw error; return true;
  }),

  // Amenity ids currently linked to a property.
  amenityIds: (propertyId) => guard("property.amenityIds", async () => {
    const s = requireSupabase();
    const { data, error } = await s.from("property_amenities").select("amenity_id").eq("property_id", propertyId);
    if (error) throw error;
    return (data || []).map((r) => r.amenity_id);
  }),

  recordView: (propertyId, userId = null) => guard("property.recordView", async () => {
    const s = requireSupabase();
    await s.from("property_views").insert({ property_id: propertyId, user_id: userId });
    return true;
  }),
};
